import { post, putBlobPublic } from '../api.js';
import { encryptFileBytesAnonymous, base64urlEncode, generateBip39Mnemonic, deriveDropWrappingKey, deriveDropLookupKey, wrapDropKey, wipe } from '../crypto.js';
import { navigate } from '../router.js';
import { lucideIcons, esc } from '../app.js';
import { getConfig } from '../config.js';
import { humanSize } from '../utils.js';
import { toast } from '../dialog.js';
import { getToken, setPickupDropKey } from '../auth-store.js';

export function renderDropLanding(container) {
  const cfg = getConfig() || {};
  const maxMB = Math.round((cfg.drop_max_bytes || 104857600) / (1024 * 1024));
  const expiryMin = cfg.drop_expiry_minutes || 60;
  const loggedIn = !!getToken();

  const inApp = loggedIn && container.id !== 'landing';

  container.innerHTML = `
    ${inApp ? '' : '<canvas id="wave-canvas"></canvas>'}
    ${inApp ? '' : `<div class="landing-header">
      <div class="landing-logo">
        <svg viewBox="0 0 32 32" width="36" height="36" style="flex-shrink:0"><defs><linearGradient id="bk-drop" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#3ecfb4"/><stop offset="100%" stop-color="#2a9d8f"/></linearGradient></defs><rect width="32" height="32" rx="7" fill="url(#bk-drop)"/><rect x="10" y="14" width="12" height="10" rx="2" fill="#0c0e14"/><path d="M12.5 14V11a3.5 3.5 0 0 1 7 0v3" fill="none" stroke="#0c0e14" stroke-width="2.2" stroke-linecap="round"/><circle cx="16" cy="19" r="1.5" fill="url(#bk-drop)"/><line x1="16" y1="20.5" x2="16" y2="22" stroke="url(#bk-drop)" stroke-width="1.5" stroke-linecap="round"/></svg>
        <div class="brand">Blind<span>Keep</span></div>
      </div>
      <a class="landing-signin" href="#/login">Sign in</a>
    </div>`}
    <div class="landing-body">
      ${inApp ? '' : '<h1>Drop it. <span>Lock it.</span> Share it.</h1>'}
      <p class="subtitle">Encrypt and share any file instantly. No account needed. Files auto-expire in ${expiryMin} minutes.</p>
      ${loggedIn ? '' : '<div class="trust-line"><i data-lucide="shield-check"></i> AES-256 + XChaCha20 <span class="trust-sep">&middot;</span> EU hosted <span class="trust-sep">&middot;</span> Open architecture</div>'}
      <div class="landing-drop" id="landingDrop">
        <div class="drop-icon"><i data-lucide="upload-cloud" style="width:28px;height:28px"></i></div>
        <p class="drop-desktop-hint">Drop a file here or click to upload</p>
        <label for="landingFileInput" class="btn btn-primary drop-mobile-btn"><i data-lucide="file-up" style="width:18px;height:18px"></i> Choose File</label>
        <span>Max ${maxMB} MB &middot; Encrypted in your browser &middot; Gone in ${expiryMin} min</span>
        <input type="file" id="landingFileInput" style="display:none">
      </div>
    </div>
    <div class="landing-divider"><span>or</span></div>
    <a class="landing-pickup" href="#/pickup">
      <div class="pickup-icon"><i data-lucide="package-open" style="width:22px;height:22px"></i></div>
      <div class="pickup-text">
        <strong>Pick up a file</strong>
        <span>Enter your 12-word passphrase to decrypt</span>
      </div>
      <i data-lucide="chevron-right" style="width:18px;height:18px" class="pickup-arrow"></i>
    </a>
    <div class="banner-strip" ${loggedIn ? 'style="display:none"' : ''}>
      <div class="banner-track">
        <a class="banner-item banner-link" data-feature="feature-vault-detail"><div class="banner-icon"><i data-lucide="shield-check"></i></div><span><strong>Zero knowledge</strong><em>we can't read your files</em></span></a>
        <a class="banner-item banner-link" data-feature="feature-sharing-detail"><div class="banner-icon"><i data-lucide="key-round"></i></div><span><strong>12-word passphrase</strong><em>link alone is useless</em></span></a>
        <a class="banner-item banner-link" data-feature="feature-vault-detail"><div class="banner-icon"><i data-lucide="timer"></i></div><span><strong>Auto-expires</strong><em>gone in ${expiryMin} minutes</em></span></a>
        <a class="banner-item banner-link" data-feature="feature-will-detail"><div class="banner-icon"><i data-lucide="scroll-text"></i></div><span><strong>Digital will</strong><em>dead man's switch built in</em></span></a>
        <a class="banner-item banner-link" data-feature="feature-pricing-detail"><div class="banner-icon"><i data-lucide="credit-card"></i></div><span><strong>Pay as you go</strong><em>no subscriptions ever</em></span></a>
        <a class="banner-item banner-link" data-feature="feature-vault-detail"><div class="banner-icon"><i data-lucide="flag"></i></div><span><strong>EU hosted</strong><em>your data stays in Europe</em></span></a>
        <a class="banner-item banner-link" data-feature="feature-vault-detail"><div class="banner-icon"><i data-lucide="shield-check"></i></div><span><strong>Zero knowledge</strong><em>we can't read your files</em></span></a>
        <a class="banner-item banner-link" data-feature="feature-sharing-detail"><div class="banner-icon"><i data-lucide="key-round"></i></div><span><strong>12-word passphrase</strong><em>link alone is useless</em></span></a>
        <a class="banner-item banner-link" data-feature="feature-vault-detail"><div class="banner-icon"><i data-lucide="timer"></i></div><span><strong>Auto-expires</strong><em>gone in ${expiryMin} minutes</em></span></a>
        <a class="banner-item banner-link" data-feature="feature-will-detail"><div class="banner-icon"><i data-lucide="scroll-text"></i></div><span><strong>Digital will</strong><em>dead man's switch built in</em></span></a>
        <a class="banner-item banner-link" data-feature="feature-pricing-detail"><div class="banner-icon"><i data-lucide="credit-card"></i></div><span><strong>Pay as you go</strong><em>no subscriptions ever</em></span></a>
        <a class="banner-item banner-link" data-feature="feature-vault-detail"><div class="banner-icon"><i data-lucide="flag"></i></div><span><strong>EU hosted</strong><em>your data stays in Europe</em></span></a>
      </div>
    </div>
    <section class="pricing-section" id="pricing-section" ${loggedIn ? 'style="display:none"' : ''}>
      <h2 class="pricing-heading">Simple, honest pricing</h2>
      <p class="pricing-subheading">No subscriptions. No surprises. Pay only for what you store.</p>
      <div class="pricing-grid">
        <div class="pricing-card pricing-card-free">
          <div class="pricing-badge">Free</div>
          <div class="pricing-icon"><i data-lucide="gift" style="width:24px;height:24px"></i></div>
          <h3 class="pricing-tier">Starter</h3>
          <div class="pricing-amount">
            <span class="pricing-currency">&euro;</span>
            <span class="pricing-value">0</span>
          </div>
          <div class="pricing-unit">1 GB&middot;month included</div>
          <ul class="pricing-features">
            <li><span class="pricing-check">&check;</span> Encrypted vault</li>
            <li><span class="pricing-check">&check;</span> Secure sharing with grants</li>
            <li><span class="pricing-check">&check;</span> Digital will</li>
            <li><span class="pricing-check">&check;</span> No credit card needed</li>
          </ul>
          <a href="#/login" class="btn btn-primary btn-pricing">Create free account</a>
        </div>
        <div class="pricing-card pricing-card-standard">
          <div class="pricing-icon"><i data-lucide="hard-drive" style="width:24px;height:24px"></i></div>
          <h3 class="pricing-tier">1 GB&middot;year</h3>
          <div class="pricing-amount">
            <span class="pricing-currency">&euro;</span>
            <span class="pricing-value">5</span>
          </div>
          <div class="pricing-unit">one-time top-up</div>
          <ul class="pricing-features">
            <li><span class="pricing-check">&check;</span> Everything in Free</li>
            <li><span class="pricing-check">&check;</span> Store 1 GB for a full year</li>
            <li><span class="pricing-check">&check;</span> Or 500 MB for two years</li>
            <li><span class="pricing-check">&check;</span> Balance never expires</li>
          </ul>
          <a href="#/login" class="btn btn-secondary btn-pricing">Sign in to top up</a>
        </div>
        <div class="pricing-card pricing-card-featured">
          <div class="pricing-popular">Best value</div>
          <div class="pricing-icon"><i data-lucide="database" style="width:24px;height:24px"></i></div>
          <h3 class="pricing-tier">10 GB&middot;years</h3>
          <div class="pricing-amount">
            <span class="pricing-currency">&euro;</span>
            <span class="pricing-value">25</span>
          </div>
          <div class="pricing-unit">one-time top-up <span class="pricing-discount">50% off</span></div>
          <ul class="pricing-features">
            <li><span class="pricing-check">&check;</span> Everything in Free</li>
            <li><span class="pricing-check">&check;</span> Store 10 GB for a full year</li>
            <li><span class="pricing-check">&check;</span> Or 1 GB for ten years</li>
            <li><span class="pricing-check">&check;</span> Balance never expires</li>
          </ul>
          <a href="#/login" class="btn btn-primary btn-pricing">Sign in to top up</a>
        </div>
      </div>
      <p class="pricing-footnote">Drops are always free &mdash; no account needed. Vault balance is measured in GB&middot;hours and never expires.</p>
    </section>
    <section class="features-section" id="features-section" ${loggedIn ? 'style="display:none"' : ''}>
      <h2 class="features-heading">Beyond drops</h2>
      <div class="features-grid">
        <div class="feature-card">
          <div class="feature-icon"><i data-lucide="lock" style="width:24px;height:24px"></i></div>
          <h3>Encrypted Vault</h3>
          <p>Store files & secrets forever. AES-256 + XChaCha20. Only you hold the keys.</p>
          <a class="btn btn-primary btn-sm feature-learn-more" data-target="feature-vault-detail" style="margin-top:auto">Learn more &darr;</a>
        </div>
        <div class="feature-card">
          <div class="feature-icon"><i data-lucide="share-2" style="width:24px;height:24px"></i></div>
          <h3>Secure Sharing</h3>
          <p>Share with anyone. Set view limits, expiry, IP restrictions. Revoke anytime.</p>
          <a class="btn btn-secondary btn-sm feature-learn-more" data-target="feature-sharing-detail" style="margin-top:auto">Learn more &darr;</a>
        </div>
        <div class="feature-card">
          <div class="feature-icon"><i data-lucide="scroll-text" style="width:24px;height:24px"></i></div>
          <h3>Digital Will</h3>
          <p>Name an heir. Dead man's switch checks on you. Keys transfer automatically.</p>
          <a class="btn btn-secondary btn-sm feature-learn-more" data-target="feature-will-detail" style="margin-top:auto">Learn more &darr;</a>
        </div>
      </div>

      <div class="feature-detail" id="feature-vault-detail" style="display:none">
        <div class="feature-detail-inner">
          <h3><i data-lucide="lock" style="width:18px;height:18px"></i> Encrypted Vault</h3>
          <p>Your vault is a permanent, encrypted store for files and secrets. Everything is encrypted in your browser before it leaves your device — BlindKeep never sees your data.</p>
          <ul>
            <li><strong>AES-256 + XChaCha20-Poly1305</strong> — military-grade encryption with authenticated ciphertexts</li>
            <li><strong>Zero knowledge</strong> — your master key is derived from your password client-side. We store only an auth key, never the encryption key</li>
            <li><strong>Files and text</strong> — store documents, credentials, recovery phrases, private keys, or any secret</li>
            <li><strong>EU hosted</strong> — encrypted blobs stored in European data centres. Even if seized, the data is indecipherable</li>
            <li><strong>No limits on time</strong> — unlike drops, vault items persist until you delete them</li>
          </ul>
          <a href="#/login" class="btn btn-primary btn-sm" style="margin-top:8px">Create vault &rarr;</a>
        </div>
      </div>

      <div class="feature-detail" id="feature-sharing-detail" style="display:none">
        <div class="feature-detail-inner">
          <h3><i data-lucide="share-2" style="width:18px;height:18px"></i> Secure Sharing</h3>
          <p>Vault items can be shared as <strong>grants</strong> — encrypted links that give others time-limited, revocable access to your files and secrets.</p>
          <ul>
            <li><strong>View limits</strong> — allow one-time or N-time access, then the grant self-destructs</li>
            <li><strong>Expiry dates</strong> — set a date after which the link stops working</li>
            <li><strong>IP restrictions</strong> — lock a grant to a specific network</li>
            <li><strong>Revoke anytime</strong> — kill access instantly, even if the link is already shared</li>
            <li><strong>Access alerts</strong> — get notified in your audit log every time someone opens a grant</li>
            <li><strong>No account needed</strong> — recipients decrypt in their browser with a link-secret, no signup required</li>
          </ul>
          <a href="#/login" class="btn btn-primary btn-sm" style="margin-top:8px">Create vault &rarr;</a>
        </div>
      </div>

      <div class="feature-detail" id="feature-will-detail" style="display:none">
        <div class="feature-detail-inner">
          <h3><i data-lucide="scroll-text" style="width:18px;height:18px"></i> Digital Will</h3>
          <p>Choose an heir and select which vault items they should receive. Your encryption keys are wrapped so that only the heir can unwrap them — and only after the dead man's switch triggers.</p>
          <ul>
            <li><strong>Dead man's switch</strong> — BlindKeep checks in with you on a schedule you choose (weekly, monthly, quarterly). Miss enough check-ins and the will activates</li>
            <li><strong>Heir receives an email</strong> — with a secure link to decrypt and download the items you selected</li>
            <li><strong>Zero knowledge</strong> — BlindKeep never sees the contents. Key wrapping uses X25519 + XChaCha20 so only the heir's credentials can unlock</li>
            <li><strong>Update anytime</strong> — change your heir, items, or check-in interval whenever you want</li>
          </ul>
          <a href="#/login" class="btn btn-primary btn-sm" style="margin-top:8px">Create vault &rarr;</a>
        </div>
      </div>

      <div class="feature-detail" id="feature-pricing-detail" style="display:none">
        <div class="feature-detail-inner">
          <h3><i data-lucide="credit-card" style="width:18px;height:18px"></i> Pay As You Go</h3>
          <p>No subscriptions, no tiers, no surprises. You pay only for what you store, measured in GB&middot;years.</p>
          <ul>
            <li><strong>Free tier included</strong> — enough for thousands of text secrets or a handful of files, forever</li>
            <li><strong>&euro;5 per GB&middot;year, &euro;25 per 10 GB&middot;years</strong> — store 1 GB for a year, or 10 GB for a year at a discount</li>
            <li><strong>No recurring charges</strong> — top up your balance when you need more. Unused balance never expires</li>
            <li><strong>Drops are always free</strong> — anonymous encrypted file sharing costs nothing, no account needed</li>
          </ul>
          <a href="#/login" class="btn btn-primary btn-sm" style="margin-top:8px">Get started free &rarr;</a>
        </div>
      </div>

      <p class="features-pricing"><strong>Pay as you go</strong> — free tier included. Then &euro;5 per GB&middot;year. No subscriptions.</p>
    </section>
    ${loggedIn ? '' : `<div class="trust-bar">
      <div class="trust-item"><i data-lucide="server" style="width:16px;height:16px"></i><span>EU data centres</span></div>
      <div class="trust-item"><i data-lucide="eye-off" style="width:16px;height:16px"></i><span>Zero knowledge architecture</span></div>
      <div class="trust-item"><i data-lucide="credit-card" style="width:16px;height:16px"></i><span>No recurring charges</span></div>
    </div>
    <div class="final-cta">
      <h2>Ready to take control of your files?</h2>
      <p>Create a free account. 1 GB&middot;month on us. No credit card.</p>
      <a href="#/login" class="btn btn-primary" style="max-width:280px;margin:0 auto">Get started free</a>
    </div>
    <div class="landing-footer"><a href="#/architecture" class="arch-footer-link">End-to-end encrypted</a> &middot; <a href="#/architecture" class="arch-footer-link">Zero knowledge</a> &middot; <a href="#/privacy" class="arch-footer-link">No tracking</a> &middot; <a href="#/compare" class="arch-footer-link">Compare</a> &middot; <a href="#/faq" class="arch-footer-link">FAQ</a></div><div class="eu-badge">\u{1F1EA}\u{1F1FA} Made &amp; operated in the EU</div>`}
  `;
  lucideIcons();

  // Animated sine wave background
  const canvas = document.getElementById('wave-canvas');
  if (canvas) {
    const ctx = canvas.getContext('2d');
    let raf;
    function resize() {
      const landing = document.getElementById('landing');
      if (!landing) return;
      canvas.width = landing.offsetWidth;
      canvas.height = landing.offsetHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    const waves = [
      { amp: 70, freq: 0.0035, speed: 0.015,  yOff: 0.38, opacity: 0.06, width: 1.8 },
      { amp: 50, freq: 0.005,  speed: -0.01,  yOff: 0.45, opacity: 0.05, width: 1.5 },
      { amp: 90, freq: 0.002,  speed: 0.008,  yOff: 0.50, opacity: 0.04, width: 2.0 },
      { amp: 55, freq: 0.004,  speed: -0.012, yOff: 0.56, opacity: 0.05, width: 1.6 },
      { amp: 75, freq: 0.003,  speed: 0.006,  yOff: 0.62, opacity: 0.04, width: 1.8 },
    ];
    let t = 0;

    function draw() {
      if (!document.contains(canvas)) {
        window.removeEventListener('resize', resize);
        return;
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const w of waves) {
        ctx.beginPath();
        ctx.strokeStyle = `rgba(62,207,180,${w.opacity})`;
        ctx.lineWidth = w.width;
        const baseY = canvas.height * w.yOff;
        for (let x = 0; x <= canvas.width; x += 2) {
          const y = baseY + Math.sin(x * w.freq + t * w.speed) * w.amp;
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      t++;
      raf = requestAnimationFrame(draw);
    }
    raf = requestAnimationFrame(draw);
  }

  // Banner links scroll to features section and open relevant detail
  document.querySelectorAll('.banner-link').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const targetId = link.dataset.feature;
      // "Pay as you go" banner scrolls to pricing section
      if (targetId === 'feature-pricing-detail') {
        document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' });
        return;
      }
      // Close all detail panels
      document.querySelectorAll('.feature-detail').forEach(d => d.style.display = 'none');
      document.querySelectorAll('.feature-learn-more').forEach(b => {
        b.innerHTML = 'Learn more &darr;';
      });
      // Open the target panel
      const target = targetId && document.getElementById(targetId);
      if (target) {
        target.style.display = '';
        // Update matching "Learn more" button if there is one
        document.querySelectorAll(`.feature-learn-more[data-target="${targetId}"]`).forEach(b => {
          b.innerHTML = 'Close &uarr;';
        });
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' });
      }
    });
  });

  // "Learn more" toggles feature detail panels
  document.querySelectorAll('.feature-learn-more').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const target = document.getElementById(btn.dataset.target);
      if (!target) return;
      const isOpen = target.style.display !== 'none';
      // Close all detail panels first
      document.querySelectorAll('.feature-detail').forEach(d => d.style.display = 'none');
      document.querySelectorAll('.feature-learn-more').forEach(b => {
        b.innerHTML = 'Learn more &darr;';
      });
      if (!isOpen) {
        target.style.display = '';
        btn.innerHTML = 'Close &uarr;';
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  });

  const dz = document.getElementById('landingDrop');
  const fi = document.getElementById('landingFileInput');

  dz.addEventListener('click', (e) => {
    if (e.target === fi || e.target.closest('.drop-mobile-btn')) return;
    fi.click();
  });
  dz.addEventListener('dragover', (e) => { e.preventDefault(); dz.classList.add('dragover'); });
  dz.addEventListener('dragleave', () => dz.classList.remove('dragover'));
  dz.addEventListener('drop', (e) => {
    e.preventDefault();
    dz.classList.remove('dragover');
    if (e.dataTransfer.files.length) handleFile(e.dataTransfer.files[0]);
  });
  fi.addEventListener('change', () => { if (fi.files.length) handleFile(fi.files[0]); });

  async function handleFile(file) {
    // Show encrypt overlay
    const overlay = document.createElement('div');
    overlay.className = 'encrypt-overlay';
    overlay.innerHTML = `<div class="encrypt-box"><div class="spinner"></div><p>Encrypting ${esc(file.name)}...</p><div class="mono">XChaCha20-Poly1305</div></div>`;
    document.body.appendChild(overlay);

    try {
      const bytes = new Uint8Array(await file.arrayBuffer());

      // Prepend encrypted metadata header: [4-byte length][JSON]{file bytes}
      const meta = JSON.stringify({ name: file.name, type: file.type, size: file.size });
      const metaBytes = new TextEncoder().encode(meta);
      const header = new Uint8Array(4);
      new DataView(header.buffer).setUint32(0, metaBytes.length);
      const payload = new Uint8Array(4 + metaBytes.length + bytes.length);
      payload.set(header);
      payload.set(metaBytes, 4);
      payload.set(bytes, 4 + metaBytes.length);

      const enc = encryptFileBytesAnonymous(payload);

      // Generate BIP39 mnemonic, derive lookup key and wrap the drop key
      overlay.querySelector('p').textContent = 'Generating passphrase...';
      const mnemonic = await generateBip39Mnemonic();
      const wrappingKey = await deriveDropWrappingKey(mnemonic, 2);
      const wrappedKey = wrapDropKey(wrappingKey, enc.key);
      wipe(wrappingKey, enc.key);
      const lookupKey = await deriveDropLookupKey(mnemonic);

      overlay.querySelector('p').textContent = 'Uploading...';

      const urlResp = await post('/drops/upload-url', { size_bytes: enc.encrypted_blob.byteLength });

      // Try direct S3 upload; fall back to API proxy on network failure
      let directFailed = false;
      try {
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.timeout = 30000;
          xhr.open('PUT', urlResp.upload_url);
          xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`));
          xhr.onerror = () => { directFailed = true; reject(new Error('Upload failed')); };
          xhr.ontimeout = () => { directFailed = true; reject(new Error('Timeout')); };
          xhr.send(enc.encrypted_blob);
        });
      } catch (err) {
        if (!directFailed || !urlResp.proxy_upload) throw err;
        await putBlobPublic(`/drops/upload/${urlResp.s3_key}`, enc.encrypted_blob);
      }

      const drop = await post('/drops', {
        s3_key: urlResp.s3_key,
        size_bytes: enc.encrypted_blob.byteLength,
        nonce: enc.nonce,
        wrapped_drop_key: Array.from(wrappedKey),
        lookup_key: lookupKey,
        drop_key_version: 2,
      });

      overlay.remove();

      const slugUrl = `${window.location.origin}${window.location.pathname}#/pickup/${mnemonic.split(' ').join('-')}`;
      const expiresAt = new Date(drop.expires_at);
      const rawKeyB64 = base64urlEncode(enc.key);

      showShareResult(slugUrl, mnemonic, rawKeyB64, file.name, humanSize(file.size), expiresAt, container, expiryMin, drop.id, loggedIn);
    } catch (err) {
      overlay.remove();
      toast('Upload failed: ' + err.message);
    }
  }
}

function showShareResult(slugUrl, mnemonic, rawKeyB64, filename, sizeStr, expiresAt, container, expiryMin, dropId, loggedIn) {
  const words = mnemonic.split(' ');
  const el = document.createElement('div');
  el.className = 'share-result';
  el.innerHTML = `
    <div class="share-box">
      <div class="check-circle check-pop"><i data-lucide="check" style="width:28px;height:28px"></i></div>
      <h3>File Encrypted & Uploaded</h3>
      <p style="font-size:14px;color:var(--text-dim)">Share this link — the passphrase is encoded in the URL.</p>

      <div style="width:100%;margin-top:16px">
        <label style="font-size:13px;font-weight:600;color:var(--text-dim);text-transform:uppercase;letter-spacing:0.05em">Share Link</label>
        <div class="copy-link-row" style="margin-top:6px">
          <input value="${esc(slugUrl)}" readonly id="shareUrlInput">
          <button class="btn btn-secondary btn-sm" id="copyLinkBtn">Copy link</button>
          ${navigator.share ? `<button class="btn btn-primary btn-sm" id="nativeShareBtn" title="Share"><i data-lucide="share" style="width:16px;height:16px"></i></button>` : ''}
        </div>
      </div>

      <details style="width:100%;margin-top:16px;cursor:pointer">
        <summary style="font-size:13px;font-weight:600;color:var(--text-dim);list-style:none;display:flex;align-items:center;gap:6px"><i data-lucide="key-round" style="width:14px;height:14px"></i> Passphrase (12 words) <span style="font-weight:400;font-size:12px">(for sharing verbally)</span></summary>
        <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:6px;margin-top:8px" id="mnemonicDisplay">
          ${words.map((w, i) => `<div style="padding:6px 4px;border-radius:6px;background:var(--bg-secondary,#1a1c24);border:1px solid var(--border,#2a2d38);font-family:'JetBrains Mono',monospace;font-size:13px;text-align:center"><span style="color:var(--text-dim);font-size:11px">${i+1}.</span> ${esc(w)}</div>`).join('')}
        </div>
        <button class="btn btn-secondary btn-sm" id="copyWordsBtn" style="margin-top:8px;width:100%">Copy words</button>
      </details>

      <div class="countdown-bar" style="margin-top:16px">
        <div class="timer" id="dropTimer">${String(expiryMin - 1).padStart(2,'0')}:59</div>
        <div class="timer-label">until auto-delete</div>
      </div>

      <div class="email-share" style="margin-top:20px;width:100%">
        <p style="font-size:14px;font-weight:600;margin-bottom:4px">Email the pickup link</p>
        <p style="font-size:12px;color:var(--text-dim);margin-bottom:8px">The full share link (with passphrase) will be sent.</p>
        <div id="emailChips" style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:8px"></div>
        <div style="display:flex;gap:8px">
          <input type="email" id="emailInput" placeholder="recipient@example.com" style="flex:1;padding:8px 12px;border-radius:6px;border:1px solid var(--border,#2a2d38);background:var(--bg-secondary,#1a1c24);color:var(--text-primary,#fff);font-size:14px">
          <button class="btn btn-secondary btn-sm" id="addEmailBtn" style="white-space:nowrap">Add</button>
        </div>
        <div id="emailMsg" style="margin-top:8px"></div>
        <button class="btn btn-primary btn-sm" id="sendEmailsBtn" style="margin-top:10px;display:none;width:100%"><i data-lucide="send" style="width:14px;height:14px"></i> Send</button>
      </div>

      <div class="upgrade-card" id="upgradeCard" ${loggedIn ? 'style="display:none"' : ''}>
        <p class="upgrade-title">Want more from this share?</p>
        <ul class="upgrade-list">
          <li><span class="upgrade-check">&check;</span> Keep it longer than ${expiryMin} min</li>
          <li><span class="upgrade-check">&check;</span> Know when it's opened</li>
          <li><span class="upgrade-check">&check;</span> Revoke access anytime</li>
          <li><span class="upgrade-check">&check;</span> Limit to one-time viewing</li>
        </ul>
        <button class="btn btn-primary btn-sm" id="claimSignInBtn" style="width:100%">Create free account &rarr;</button>
        <p class="upgrade-sub">1 GB&middot;month free, no card needed</p>
      </div>

      <div class="countdown-urgency" id="countdownUrgency" style="display:none">
        <div class="urgency-timer"><span id="urgencyTimer"></span> until auto-delete</div>
        <p>Save to your vault to keep it</p>
        <button class="btn btn-primary btn-sm" id="urgencyClaimBtn" style="width:100%">Claim to vault &rarr;</button>
      </div>

      <div style="margin-top:12px"><button class="btn btn-ghost btn-sm" id="shareDoneBtn">Done</button></div>
    </div>
  `;
  document.body.appendChild(el);
  lucideIcons();

  document.getElementById('copyLinkBtn').onclick = () => {
    navigator.clipboard.writeText(slugUrl).then(() => {
      document.getElementById('copyLinkBtn').textContent = 'Copied!';
      setTimeout(() => { const b = document.getElementById('copyLinkBtn'); if (b) b.textContent = 'Copy link'; }, 2000);
    });
  };

  const copyWordsBtn = document.getElementById('copyWordsBtn');
  if (copyWordsBtn) {
    copyWordsBtn.onclick = () => {
      navigator.clipboard.writeText(mnemonic).then(() => {
        copyWordsBtn.textContent = 'Copied!';
        setTimeout(() => { if (copyWordsBtn) copyWordsBtn.textContent = 'Copy words'; }, 2000);
      });
    };
  }

  const nativeShareBtn = document.getElementById('nativeShareBtn');
  if (nativeShareBtn) {
    nativeShareBtn.onclick = () => {
      navigator.share({
        title: 'Encrypted file on BlindKeep',
        text: 'Pick up your encrypted file — opens in browser, nothing to install.',
        url: slugUrl,
      }).catch(() => {});
    };
  }

  // Email sharing
  const emails = [];
  const chipsEl = document.getElementById('emailChips');
  const emailInput = document.getElementById('emailInput');
  const addBtn = document.getElementById('addEmailBtn');
  const sendBtn = document.getElementById('sendEmailsBtn');
  const emailMsg = document.getElementById('emailMsg');

  function renderChips() {
    chipsEl.innerHTML = emails.map((e, i) =>
      `<span style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;border-radius:12px;background:var(--bg-secondary,#1a1c24);border:1px solid var(--border,#2a2d38);font-size:13px">${esc(e)}<button data-i="${i}" style="background:none;border:none;color:var(--text-secondary);cursor:pointer;padding:0 2px;font-size:16px;line-height:1">&times;</button></span>`
    ).join('');
    chipsEl.querySelectorAll('button').forEach(b => {
      b.onclick = () => { emails.splice(+b.dataset.i, 1); renderChips(); };
    });
    sendBtn.style.display = emails.length ? '' : 'none';
  }

  function addEmail() {
    const val = emailInput.value.trim().toLowerCase();
    if (!val || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      emailMsg.innerHTML = '<div style="color:#f87171;font-size:13px">Enter a valid email address.</div>';
      return;
    }
    if (emails.includes(val)) {
      emailMsg.innerHTML = '<div style="color:#f87171;font-size:13px">Already added.</div>';
      return;
    }
    if (emails.length >= 10) {
      emailMsg.innerHTML = '<div style="color:#f87171;font-size:13px">Maximum 10 recipients.</div>';
      return;
    }
    emailMsg.innerHTML = '';
    emails.push(val);
    emailInput.value = '';
    renderChips();
    emailInput.focus();
  }

  addBtn.onclick = addEmail;
  emailInput.onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } };

  sendBtn.onclick = async () => {
    if (!emails.length) return;
    sendBtn.disabled = true;
    sendBtn.textContent = 'Sending...';
    emailMsg.innerHTML = '';
    try {
      const resp = await post(`/drops/${dropId}/share`, {
        emails,
        download_url: slugUrl,
      });
      const ok = resp.results.filter(r => r.sent).length;
      const fail = resp.results.filter(r => !r.sent).length;
      emailMsg.innerHTML = `<div style="color:#4ade80;font-size:13px">Sent to ${ok} recipient${ok !== 1 ? 's' : ''}${fail ? `, ${fail} failed` : ''}.</div>`;
      emails.length = 0;
      renderChips();
    } catch (err) {
      emailMsg.innerHTML = `<div style="color:#f87171;font-size:13px">${esc(err.message)}</div>`;
    }
    sendBtn.disabled = false;
    sendBtn.innerHTML = '<i data-lucide="send" style="width:14px;height:14px"></i> Send';
    lucideIcons();
  };

  document.getElementById('claimSignInBtn').onclick = () => {
    el.remove();
    clearInterval(countdownId);
    setPickupDropKey(rawKeyB64);
    navigate(`#/drop/${dropId}/claim?key=${rawKeyB64}`);
  };

  document.getElementById('shareDoneBtn').onclick = () => {
    el.remove();
    clearInterval(countdownId);
  };

  // Urgency claim button
  const urgencyClaimBtn = document.getElementById('urgencyClaimBtn');
  if (urgencyClaimBtn) {
    urgencyClaimBtn.onclick = () => {
      el.remove();
      clearInterval(countdownId);
      setPickupDropKey(rawKeyB64);
      navigate(`#/drop/${dropId}/claim?key=${rawKeyB64}`);
    };
  }

  // Countdown
  let secs = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
  let urgencyShown = false;
  const countdownId = setInterval(() => {
    secs--;
    if (secs <= 0) { clearInterval(countdownId); secs = 0; }
    const mm = Math.floor(secs / 60), ss = secs % 60;
    const timeStr = String(mm).padStart(2, '0') + ':' + String(ss).padStart(2, '0');
    const timer = document.getElementById('dropTimer');
    if (timer) timer.textContent = timeStr;

    // Show urgency nudge at 10 minutes remaining
    if (secs <= 600 && secs > 0 && !urgencyShown) {
      urgencyShown = true;
      const bar = el.querySelector('.countdown-bar');
      if (bar) bar.classList.add('countdown-warning');
      const urgencyEl = document.getElementById('countdownUrgency');
      if (urgencyEl) urgencyEl.style.display = '';
    }
    // Update urgency timer text
    if (urgencyShown) {
      const urgencyTimer = document.getElementById('urgencyTimer');
      if (urgencyTimer) urgencyTimer.textContent = timeStr;
    }
  }, 1000);
}
